import { colors } from "@/theme/colors"
import type { FamilyNumber } from "@/theme/colors"
import styles from "@/styles/components/Badge.module.css"

/**
 * A person's two-letter badge, tinted by one of the card family colours. It
 * cycles the full tint palette (still seven, retired families included) for
 * avatar variety — the hue is decorative, not the person's family.
 */
function familyForName(name: string): FamilyNumber {
  const trimmed = name || "?"
  let hash = 0
  for (let i = 0; i < trimmed.length; i++) hash += trimmed.charCodeAt(i)
  return ((hash % 7) + 1) as FamilyNumber
}

interface BadgeProps {
  name: string
  size?: 28 | 36 | 48 | 52
  /**
   * `person` is a seat at the table — the same circle, ringed in the page
   * colour so two neighbouring seats never bleed into one another.
   *
   * Both variants are the family's full-strength bloom with white on it. The
   * printed-card look (the pale tint with its own ink) is gone: a badge is
   * always a person now, and reading as a card someone was holding was exactly
   * the confusion.
   */
  variant?: "flat" | "person"
  /** Overrides the name hash — see `familyForName`. */
  family?: FamilyNumber
}

export default function Badge({ name, size = 36, variant = "flat", family }: BadgeProps) {
  const tint = colors.family[family ?? familyForName(name)]
  const initials = (name || "?").slice(0, 2).toUpperCase()

  return (
    <span
      className={[styles.badge, styles[`size${size}`], variant === "person" ? styles.person : ""]
        .filter(Boolean)
        .join(" ")}
      style={{ background: tint.bloom, color: colors.white }}
      aria-hidden="true"
    >
      {initials}
    </span>
  )
}
